/**
 * Utility to extract safe, actionable previews from AI suggestions across tools
 * before applying irreversible changes to persistent storage.
 */

export interface ChangePreview {
  targetTool?: string;
  actionType: 'create' | 'update' | 'replace' | 'draft' | 'info' | 'tool';
  title: string;
  items: Array<{ label: string; detail?: string }>;
  destructiveNotice?: string;
}

export interface ToolInvocation {
  tool: string;
  arguments: Record<string, unknown>;
}

/** Escape literal control characters emitted inside JSON strings by some models.
 * Keep all other JSON syntax strict; never evaluate model output as code.
 */
function escapeStringControls(source: string): string {
  let quoted = false;
  let escaped = false;
  let result = '';
  for (const char of source) {
    if (quoted && char.charCodeAt(0) < 32) {
      result += `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`;
      continue;
    }
    result += char;
    if (escaped) escaped = false;
    else if (quoted && char === '\\') escaped = true;
    else if (char === '"') quoted = !quoted;
  }
  return result;
}

/**
 * Extract one JSON tool call without relying on a greedy regular expression.
 * Tool arguments commonly contain Markdown, which may itself contain braces.
 */
export function parseToolInvocation(content: string): ToolInvocation | null {
  const fenced = content.trim().match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  const source = fenced ? fenced[1].trim() : content.trim();

  for (let start = source.indexOf('{'); start >= 0; start = source.indexOf('{', start + 1)) {
    let depth = 0;
    let quoted = false;
    let escaped = false;
    for (let index = start; index < source.length; index += 1) {
      const char = source[index];
      if (quoted) {
        if (escaped) escaped = false;
        else if (char === '\\') escaped = true;
        else if (char === '"') quoted = false;
        continue;
      }
      if (char === '"') quoted = true;
      else if (char === '{') depth += 1;
      else if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          try {
            const parsed: unknown = JSON.parse(escapeStringControls(source.slice(start, index + 1)));
            if (typeof parsed === 'object' && parsed !== null &&
              typeof (parsed as { tool?: unknown }).tool === 'string' &&
              typeof (parsed as { arguments?: unknown }).arguments === 'object' &&
              (parsed as { arguments?: unknown }).arguments !== null &&
              !Array.isArray((parsed as { arguments?: unknown }).arguments)) {
              return parsed as ToolInvocation;
            }
          } catch {
            // Keep looking: prose can contain brace-delimited non-JSON text.
          }
          break;
        }
      }
    }
  }
  return null;
}

/**
 * Extract conversational prose by removing tool invocation JSON blocks (fenced or bare).
 * Returns an empty string if the content consisted purely of a tool call.
 */
export function extractProseWithoutTool(content: string): string {
  if (!content) return '';

  // 1. Check for fenced code block containing a tool invocation
  const fencedRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
  let match: RegExpExecArray | null;
  while ((match = fencedRegex.exec(content)) !== null) {
    if (parseToolInvocation(match[0])) {
      const before = content.slice(0, match.index);
      const after = content.slice(match.index + match[0].length);
      return `${before.trim()} ${after.trim()}`.trim();
    }
  }

  // 2. Check for bare JSON object
  const trimmed = content.trim();
  for (let start = trimmed.indexOf('{'); start >= 0; start = trimmed.indexOf('{', start + 1)) {
    let depth = 0;
    let quoted = false;
    let escaped = false;
    for (let index = start; index < trimmed.length; index += 1) {
      const char = trimmed[index];
      if (quoted) {
        if (escaped) escaped = false;
        else if (char === '\\') escaped = true;
        else if (char === '"') quoted = false;
        continue;
      }
      if (char === '"') quoted = true;
      else if (char === '{') depth += 1;
      else if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          try {
            const candidate = trimmed.slice(start, index + 1);
            const parsed = parseToolInvocation(candidate);
            if (
              typeof parsed === 'object' &&
              parsed !== null &&
              typeof (parsed as { tool?: unknown }).tool === 'string' &&
              typeof (parsed as { arguments?: unknown }).arguments === 'object'
            ) {
              const before = trimmed.slice(0, start).trim();
              const after = trimmed.slice(index + 1).trim();
              return `${before} ${after}`.trim();
            }
          } catch {
            // Keep looking
          }
          break;
        }
      }
    }
  }

  return content.trim();
}

export function parseChangePreview(content: string, appId?: string): ChangePreview | null {
  if (!content) return null;
  const trimmed = content.trim();

  // 0. Tool Call Invocation JSON block
  const toolCall = parseToolInvocation(trimmed);
  if (toolCall) {
          const items: Array<{ label: string; detail?: string }> = Object.entries(toolCall.arguments).map(
            ([k, v]) => ({
              label: String(k),
              detail: typeof v === 'object' ? JSON.stringify(v) : String(v ?? ''),
            })
          );
          return {
            targetTool: appId || 'Active Tool',
            actionType: 'tool',
            title: `Execute Action: ${String(toolCall.tool)}`,
            items,
          };
  }

  // 1. Habits: JSON payload matching habit creation
  if (appId === '@yada/app-habits' || trimmed.startsWith('{') && trimmed.includes('"rhythmType"')) {
    try {
      const match = trimmed.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        if (parsed.name) {
          const items: Array<{ label: string; detail?: string }> = [
            { label: 'Habit Name', detail: parsed.name },
            { label: 'Rhythm', detail: parsed.rhythmType || 'daily' },
            { label: 'Tracking Style', detail: parsed.trackingStyle || 'gentle' },
          ];
          if (parsed.color) items.push({ label: 'Color', detail: parsed.color });
          if (parsed.notes) items.push({ label: 'Notes', detail: parsed.notes });

          return {
            targetTool: 'Habits',
            actionType: 'create',
            title: `Create new habit "${parsed.name}"`,
            items,
          };
        }
      }
    } catch {
      // Not JSON or parse error — fallback
    }
  }

  // 2. Meal Planner: Weekly meal plan generation
  if (appId === '@yada/app-meal-planner') {
    // Check for weekly meal plan date headers
    const dateHeaders = trimmed.match(/\((\d{4}-\d{2}-\d{2})\)/g);
    if (dateHeaders && dateHeaders.length >= 2) {
      const lines = trimmed.split('\n');
      const items: Array<{ label: string; detail?: string }> = [];
      let currentDate = '';
      for (const line of lines) {
        const dMatch = line.match(/\((\d{4}-\d{2}-\d{2})\)/);
        if (dMatch) {
          currentDate = dMatch[1];
        } else if (currentDate) {
          const mealMatch = line.match(/^\s*(\w+):\s*(.+)/);
          if (mealMatch) {
            items.push({
              label: `${currentDate} (${mealMatch[1]})`,
              detail: mealMatch[2].trim(),
            });
          }
        }
      }

      return {
        targetTool: 'Meal Planner',
        actionType: 'replace',
        title: `Plan ${items.length} meals for the week`,
        items: items.slice(0, 7), // preview first 7
        destructiveNotice: 'Applying will replace currently planned meals in this date range.',
      };
    }
  }

  // 3. Finance: Invoice notes update or payment reminder
  if (appId === '@yada/app-finance') {
    if (trimmed.length < 300 && !trimmed.toLowerCase().includes('dear ')) {
      return {
        targetTool: 'Finance',
        actionType: 'update',
        title: 'Update invoice notes',
        items: [{ label: 'Proposed notes', detail: trimmed.slice(0, 160) + (trimmed.length > 160 ? '…' : '') }],
        destructiveNotice: 'Applying will overwrite the current notes on the selected invoice.',
      };
    }
    return {
      targetTool: 'Finance',
      actionType: 'draft',
      title: 'Draft reminder / communication',
      items: [{ label: 'Preview excerpt', detail: trimmed.slice(0, 140) + (trimmed.length > 140 ? '…' : '') }],
    };
  }

  // 4. Contacts: Email/message draft
  if (appId === '@yada/app-contacts') {
    return {
      targetTool: 'Contacts',
      actionType: 'draft',
      title: 'Communication draft',
      items: [{ label: 'Draft message', detail: trimmed.slice(0, 150) + (trimmed.length > 150 ? '…' : '') }],
    };
  }

  return null;
}
